import { useState, useRef, useEffect } from 'react';
import type { ChatMessage, ChatChannel } from '@flip7/shared';
import { CHAT_MAX_LENGTH } from '@flip7/shared';

interface ChatPanelProps {
  roomMessages: ChatMessage[];
  spectatorMessages: ChatMessage[];
  unreadRoom: number;
  unreadSpectator: number;
  isSpectator: boolean;
  onSend: (channel: ChatChannel, content: string) => void;
  onClearUnread: (channel: ChatChannel) => void;
}

export function ChatPanel({
  roomMessages,
  spectatorMessages,
  unreadRoom,
  unreadSpectator,
  isSpectator,
  onSend,
  onClearUnread,
}: ChatPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState<ChatChannel>('room');
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const messages = activeChannel === 'room' ? roomMessages : spectatorMessages;
  const totalUnread = unreadRoom + unreadSpectator;

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      onClearUnread(activeChannel);
    }
  }, [messages.length, isOpen, activeChannel, onClearUnread]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(activeChannel, input);
    setInput('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const switchChannel = (channel: ChatChannel) => {
    setActiveChannel(channel);
    onClearUnread(channel);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => {
          setIsOpen(true);
          onClearUnread(activeChannel);
        }}
        className="fab chat-fab"
        aria-label="Open chat"
      >
        {'\uD83D\uDCAC'}
        {totalUnread > 0 && (
          <span className="chat-fab__badge">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="chat-panel slide-up">
      {/* Header */}
      <div className="chat-panel__header">
        <button
          onClick={() => switchChannel('room')}
          className={`chat-panel__tab${activeChannel === 'room' ? ' chat-panel__tab--active' : ''}`}
        >
          Room
          {unreadRoom > 0 && activeChannel !== 'room' && (
            <span className="chat-panel__unread">({unreadRoom})</span>
          )}
        </button>
        {isSpectator && (
          <button
            onClick={() => switchChannel('spectator')}
            className={`chat-panel__tab${activeChannel === 'spectator' ? ' chat-panel__tab--active' : ''}`}
          >
            Spectators
            {unreadSpectator > 0 && activeChannel !== 'spectator' && (
              <span className="chat-panel__unread">({unreadSpectator})</span>
            )}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button className="chat-panel__close" onClick={() => setIsOpen(false)} aria-label="Close chat">
          {'\u2715'}
        </button>
      </div>

      {/* Messages */}
      <div className="chat-panel__messages">
        {messages.length === 0 && (
          <div className="chat-panel__empty">No messages yet</div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className="chat-msg">
            {msg.senderRole === 'system' ? (
              <span className="chat-msg__system">{msg.content}</span>
            ) : (
              <>
                <span className={`chat-msg__sender${msg.senderRole === 'spectator' ? ' chat-msg__sender--spectator' : ''}`}>
                  {msg.senderName}
                  {msg.senderRole === 'spectator' && (
                    <span className="chat-msg__role">(spectator)</span>
                  )}
                </span>
                <span className="chat-msg__content">{msg.content}</span>
              </>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-panel__input-row">
        <input
          ref={inputRef}
          className="chat-panel__input"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value.slice(0, CHAT_MAX_LENGTH))}
          onKeyDown={handleKeyDown}
          placeholder={activeChannel === 'spectator' ? 'Spectators only...' : 'Message...'}
          maxLength={CHAT_MAX_LENGTH}
        />
        <button onClick={handleSend} disabled={!input.trim()} className="btn btn-primary chat-panel__send-btn">
          Send
        </button>
      </div>
    </div>
  );
}
